// frontend/src/utils/etaWaypointHelpers.js
// Waypoint-related helper utilities for ETA Display

import { Navigation, CheckCircle, Flag } from 'lucide-react';

/**
 * Get waypoint state metadata (label, classes, icon)
 */
export const getWaypointStateMeta = (state, t) => {
    switch (state) {
        case 'approaching':
            return {
                label: t('waypointStates.approaching'),
                classes: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200',
                icon: <Navigation className="h-3 w-3" />
            };
        case 'dwelling':
            return {
                label: t('waypointStates.dwelling'),
                classes: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200',
                icon: <CheckCircle className="h-3 w-3" />
            };
        case 'departing':
            return {
                label: t('waypointStates.departing'),
                classes: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200',
                icon: <Flag className="h-3 w-3" />
            };
        case 'passed':
            return {
                label: t('waypointStates.passed'),
                classes: 'bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200',
                icon: <CheckCircle className="h-3 w-3" />
            };
        default:
            return null;
    }
};

/**
 * Check if waypoint has boarding info
 */
export const hasBoardingInfo = (waypoint) => {
    return !!(
        waypoint.boarding_location_info ||
        waypoint.boarding_location_url ||
        waypoint.boarding_location_image
    );
};

/**
 * Resolve boarding image URL to full path
 */
export const resolveBoardingImageUrl = (imagePath) => {
    if (!imagePath) {
        return null;
    }

    if (/^https?:\/\//i.test(imagePath)) {
        return imagePath;
    }

    const sanitizedFilename = imagePath
        .replace(/^\/?uploads\/stops\//i, '')
        .replace(/^\//, '');
    const baseUrl = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

    if (!sanitizedFilename) {
        return null;
    }

    if (imagePath.startsWith('/')) {
        return baseUrl ? `${baseUrl}${imagePath}` : imagePath;
    }

    if (baseUrl) {
        return `${baseUrl}/uploads/stops/${sanitizedFilename}`;
    }

    return `/uploads/stops/${sanitizedFilename}`;
};

/**
 * Get translated waypoint name with fallback to original
 */
export const getTranslatedWaypointName = (waypointId, originalName, waypointTranslations, entitySettings, currentEntity) => {
    // If multi-language is not enabled, return original name
    if (!entitySettings?.multi_language_enabled && !currentEntity?.multi_language_enabled) {
        return originalName;
    }

    // Check if we have a translation for this waypoint's name
    const translation = waypointTranslations[waypointId]?.name;
    if (translation && translation.isTranslated) {
        return translation.content;
    }

    // Fallback to original name
    return originalName;
};

/**
 * Get translated boarding info with fallback to original
 */
export const getTranslatedBoardingInfo = (waypointId, originalBoardingInfo, waypointTranslations, entitySettings, currentEntity) => {
    // If multi-language is not enabled, return original boarding info
    if (!entitySettings?.multi_language_enabled && !currentEntity?.multi_language_enabled) {
        return originalBoardingInfo;
    }

    // Check if we have a translation for this waypoint's boarding info
    const translation = waypointTranslations[waypointId]?.boarding_location_info;
    if (translation && translation.isTranslated) {
        return translation.content;
    }

    // Fallback to original boarding info
    return originalBoardingInfo;
};

/**
 * Check if a preference is enabled (handles various formats)
 */
export const isPreferenceEnabled = (value) => {
    // Preference values sometimes arrive as truthy strings/integers; normalize to a boolean toggle.
    if (value === true || value === 1) {
        return true;
    }
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return normalized === 'true' || normalized === '1';
    }
    return false;
};